import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { RendererProps } from "../FilePreviewModal";
import { getExtension } from "../previewTypes";
import { useTypedBlobUrl } from "../useTypedBlobUrl";

const EXT_MIME_OVERRIDES: Record<string, string> = {
  svg: "image/svg+xml",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  ico: "image/x-icon",
  avif: "image/avif",
};

const MIN_SCALE = 0.1;
const MAX_SCALE = 8;
const ZOOM_STEP = 1.25;

const ImageRenderer: React.FC<RendererProps> = ({ blob, blobUrl, fileName }) => {
  const ext = useMemo(() => getExtension(fileName), [fileName]);
  const targetMime = useMemo(() => {
    const override = EXT_MIME_OVERRIDES[ext];
    if (override) return override;
    if (blob?.type && blob.type.startsWith("image/")) return blob.type;
    return "image/*";
  }, [ext, blob]);

  const url = useTypedBlobUrl(
    blob,
    blobUrl,
    targetMime === "image/*" ? blob?.type || "image/png" : targetMime,
  );

  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef<{
    x: number;
    y: number;
    tx: number;
    ty: number;
  } | null>(null);

  const handleZoomIn = useCallback(
    () => setScale((s) => Math.min(s * ZOOM_STEP, MAX_SCALE)),
    [],
  );
  const handleZoomOut = useCallback(
    () => setScale((s) => Math.max(s / ZOOM_STEP, MIN_SCALE)),
    [],
  );
  const handleReset = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (scale === 1) {
      setTranslate({ x: 0, y: 0 });
    }
  }, [scale]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (scale === 1) return;
      e.preventDefault();
      (e.currentTarget as HTMLDivElement).setPointerCapture?.(e.pointerId);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        tx: translate.x,
        ty: translate.y,
      };
      setDragging(true);
    },
    [scale, translate.x, translate.y],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = dragStartRef.current;
      if (!start) return;
      e.preventDefault();
      setTranslate({
        x: start.tx + (e.clientX - start.x),
        y: start.ty + (e.clientY - start.y),
      });
    },
    [],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragStartRef.current) return;
      (e.currentTarget as HTMLDivElement).releasePointerCapture?.(e.pointerId);
      dragStartRef.current = null;
      setDragging(false);
    },
    [],
  );

  const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const direction = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
    setScale((s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s * direction)));
  }, []);

  if (!url) return null;

  const cursor = scale === 1 ? "default" : dragging ? "grabbing" : "grab";

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: (t) => t.palette.grey[200],
          p: 2,
          cursor,
          touchAction: "none",
          userSelect: "none",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
      >
        <img
          src={url}
          alt={fileName}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transformOrigin: "center center",
            transition: dragging ? "none" : "transform 0.1s ease-out",
            userSelect: "none",
            pointerEvents: "none",
            willChange: "transform",
          }}
          draggable={false}
        />
      </Box>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          position: "absolute",
          bottom: 12,
          right: 12,
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 2,
          px: 0.5,
          py: 0.25,
          alignItems: "center",
        }}
      >
        <Tooltip title="Zoom out">
          <IconButton size="small" onClick={handleZoomOut}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box
          component="span"
          sx={{ fontSize: 12, minWidth: 44, textAlign: "center" }}
        >
          {Math.round(scale * 100)}%
        </Box>
        <Tooltip title="Zoom in">
          <IconButton size="small" onClick={handleZoomIn}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset">
          <IconButton size="small" onClick={handleReset}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default ImageRenderer;
